import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { miniGamesAPI } from '@/lib/api';
import { ArrowLeft, Play, Pause, RotateCcw, Trophy, Medal } from 'lucide-react';

const ROWS = 20;
const COLS = 10;
const CELL_SIZE = 30;
const COLORS = ['#000', '#FF0D72', '#0DC2FF', '#0DFF72', '#F538FF', '#FF8E0D', '#FFE138', '#3877FF'];

const SHAPES = [
  [[1, 1, 1, 1]], // I
  [[1, 1], [1, 1]], // O
  [[0, 1, 0], [1, 1, 1]], // T
  [[1, 1, 0], [0, 1, 1]], // S
  [[0, 1, 1], [1, 1, 0]], // Z
  [[1, 0, 0], [1, 1, 1]], // L
  [[0, 0, 1], [1, 1, 1]], // J
];

export default function TetrisGamePage() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [board, setBoard] = useState(Array(ROWS).fill().map(() => Array(COLS).fill(0)));
  const [currentPiece, setCurrentPiece] = useState(null);
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [gamePaused, setGamePaused] = useState(true);
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [personalBest, setPersonalBest] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const gameLoopRef = useRef(null);

  useEffect(() => {
    fetchLeaderboard();
    fetchPersonalBest();
    spawnPiece();

    // Auto-refresh leaderboard every 30 seconds
    const interval = setInterval(() => {
      fetchLeaderboard();
      fetchPersonalBest();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await miniGamesAPI.getLeaderboard('tetris', 10);
      setLeaderboard(response.data.scores || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const fetchPersonalBest = async () => {
    try {
      const response = await miniGamesAPI.getPersonalBest('tetris');
      setPersonalBest(response.data.best_score || 0);
    } catch (error) {
      console.error('Error fetching personal best:', error);
    }
  };

  const spawnPiece = () => {
    const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    const color = Math.floor(Math.random() * 7) + 1;
    setCurrentPiece({ shape, color });
    setCurrentPos({ x: Math.floor(COLS / 2) - Math.floor(shape[0].length / 2), y: 0 });
  };

  const checkCollision = (piece, pos, newBoard = board) => {
    for (let y = 0; y < piece.shape.length; y++) {
      for (let x = 0; x < piece.shape[y].length; x++) {
        if (piece.shape[y][x]) {
          const newX = pos.x + x;
          const newY = pos.y + y;
          if (newX < 0 || newX >= COLS || newY >= ROWS) return true;
          if (newY >= 0 && newBoard[newY][newX]) return true;
        }
      }
    }
    return false;
  };

  const mergePiece = () => {
    const newBoard = board.map(row => [...row]);
    for (let y = 0; y < currentPiece.shape.length; y++) {
      for (let x = 0; x < currentPiece.shape[y].length; x++) {
        if (currentPiece.shape[y][x]) {
          const boardY = currentPos.y + y;
          const boardX = currentPos.x + x;
          if (boardY >= 0) {
            newBoard[boardY][boardX] = currentPiece.color;
          }
        }
      }
    }
    return newBoard;
  };

  const clearLines = (newBoard) => {
    let linesCleared = 0;
    for (let y = ROWS - 1; y >= 0; y--) {
      if (newBoard[y].every(cell => cell !== 0)) {
        newBoard.splice(y, 1);
        newBoard.unshift(Array(COLS).fill(0));
        linesCleared++;
        y++;
      }
    }
    if (linesCleared > 0) {
      setLines(prev => prev + linesCleared);
      setScore(prev => prev + linesCleared * 100);
    }
    return newBoard;
  };

  const rotatePiece = () => {
    if (!currentPiece || gamePaused || gameOver) return;

    const rotated = currentPiece.shape[0].map((_, i) =>
      currentPiece.shape.map(row => row[i]).reverse()
    );

    const newPiece = { ...currentPiece, shape: rotated };
    if (!checkCollision(newPiece, currentPos)) {
      setCurrentPiece(newPiece);
    }
  };

  const moveHorizontal = (dir) => {
    if (!currentPiece || gamePaused || gameOver) return;

    const newPos = { ...currentPos, x: currentPos.x + dir };
    if (!checkCollision(currentPiece, newPos)) {
      setCurrentPos(newPos);
    }
  };

  const moveDown = () => {
    if (!currentPiece || gamePaused || gameOver) return;

    const newPos = { ...currentPos, y: currentPos.y + 1 };
    if (!checkCollision(currentPiece, newPos)) {
      setCurrentPos(newPos);
    } else {
      let newBoard = mergePiece();
      newBoard = clearLines(newBoard);
      setBoard(newBoard);

      const nextShape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      const nextColor = Math.floor(Math.random() * 7) + 1;
      const nextPos = { x: Math.floor(COLS / 2) - Math.floor(nextShape[0].length / 2), y: 0 };

      if (checkCollision({ shape: nextShape, color: nextColor }, nextPos, newBoard)) {
        setGameOver(true);
      } else {
        setCurrentPiece({ shape: nextShape, color: nextColor });
        setCurrentPos(nextPos);
      }
    }
  };

  const drop = () => {
    if (!currentPiece || gamePaused || gameOver) return;

    // Find the lowest position
    let newPos = { ...currentPos };
    while (!checkCollision(currentPiece, { ...newPos, y: newPos.y + 1 })) {
      newPos.y++;
    }

    // Place the piece immediately at that position
    let newBoard = board.map(row => [...row]);
    for (let y = 0; y < currentPiece.shape.length; y++) {
      for (let x = 0; x < currentPiece.shape[y].length; x++) {
        if (currentPiece.shape[y][x]) {
          const boardY = newPos.y + y;
          const boardX = newPos.x + x;
          if (boardY >= 0) {
            newBoard[boardY][boardX] = currentPiece.color;
          }
        }
      }
    }

    // Clear lines
    newBoard = clearLines(newBoard);
    setBoard(newBoard);

    // Spawn next piece
    const nextShape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    const nextColor = Math.floor(Math.random() * 7) + 1;
    const nextPos = { x: Math.floor(COLS / 2) - Math.floor(nextShape[0].length / 2), y: 0 };

    if (checkCollision({ shape: nextShape, color: nextColor }, nextPos, newBoard)) {
      setGameOver(true);
    } else {
      setCurrentPiece({ shape: nextShape, color: nextColor });
      setCurrentPos(nextPos);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameOver) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          moveHorizontal(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          moveHorizontal(1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          moveDown();
          break;
        case 'ArrowUp':
          e.preventDefault();
          rotatePiece();
          break;
        case ' ':
          e.preventDefault();
          if (!gamePaused) {
            drop();
          } else {
            setGamePaused(false);
          }
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          setGamePaused(prev => !prev);
          break;
        default:
          return;
      }

      if (gamePaused && e.key !== 'p' && e.key !== 'P') {
        setGamePaused(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPiece, currentPos, gamePaused, gameOver]);

  useEffect(() => {
    if (gamePaused || gameOver || !currentPiece) {
      if (gameLoopRef.current) clearInterval(gameLoopRef.current);
      return;
    }

    gameLoopRef.current = setInterval(moveDown, 800);
    return () => {
      if (gameLoopRef.current) clearInterval(gameLoopRef.current);
    };
  }, [gamePaused, gameOver, currentPiece, currentPos]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw board
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (board[y][x]) {
          ctx.fillStyle = COLORS[board[y][x]];
          ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE - 1, CELL_SIZE - 1);
        }
      }
    }

    // Draw current piece
    if (currentPiece) {
      ctx.fillStyle = COLORS[currentPiece.color];
      for (let y = 0; y < currentPiece.shape.length; y++) {
        for (let x = 0; x < currentPiece.shape[y].length; x++) {
          if (currentPiece.shape[y][x]) {
            ctx.fillRect(
              (currentPos.x + x) * CELL_SIZE,
              (currentPos.y + y) * CELL_SIZE,
              CELL_SIZE - 1,
              CELL_SIZE - 1
            );
          }
        }
      }
    }

    // Draw grid
    ctx.strokeStyle = '#2a2a2a';
    for (let i = 0; i <= COLS; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL_SIZE, 0);
      ctx.lineTo(i * CELL_SIZE, ROWS * CELL_SIZE);
      ctx.stroke();
    }
    for (let i = 0; i <= ROWS; i++) {
      ctx.beginPath();
      ctx.moveTo(0, i * CELL_SIZE);
      ctx.lineTo(COLS * CELL_SIZE, i * CELL_SIZE);
      ctx.stroke();
    }

    if (gameOver) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Game Over!', canvas.width / 2, canvas.height / 2 - 20);
      ctx.font = '24px Arial';
      ctx.fillText(`Score: ${score}`, canvas.width / 2, canvas.height / 2 + 20);
    }
  }, [board, currentPiece, currentPos, gameOver, score]);

  const handleRestart = () => {
    setBoard(Array(ROWS).fill().map(() => Array(COLS).fill(0)));
    spawnPiece();
    setGameOver(false);
    setGamePaused(true);
    setScore(0);
    setLines(0);
  };

  const handleSubmitScore = async () => {
    try {
      setSubmitting(true);
      await miniGamesAPI.submitScore('tetris', score);
      await fetchLeaderboard();
      await fetchPersonalBest();
      handleRestart();
    } catch (error) {
      console.error('Error submitting score:', error);
      alert('Fout bij opslaan score: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
    });
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" onClick={() => navigate('/mini-games')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug
        </Button>
        <div>
          <h1 className="text-3xl font-bold">🧱 Tetris</h1>
          <p className="text-gray-400">Stapel de blokken en maak lijnen!</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card className="border-blue-500/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex gap-4">
                  <div>
                    Score: <span className="text-blue-400 font-bold">{score}</span>
                  </div>
                  <div>
                    Lijnen: <span className="text-blue-400 font-bold">{lines}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {!gameOver && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setGamePaused(!gamePaused)}
                    >
                      {gamePaused ? (
                        <><Play className="w-4 h-4 mr-2" /> Start</>
                      ) : (
                        <><Pause className="w-4 h-4 mr-2" /> Pauze</>
                      )}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={handleRestart}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Opnieuw
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <canvas
                  ref={canvasRef}
                  width={COLS * CELL_SIZE}
                  height={ROWS * CELL_SIZE}
                  className="border-2 border-blue-500/50 rounded"
                />
              </div>

              <div className="mt-4 p-4 bg-gray-800/50 rounded-lg">
                <h3 className="font-semibold mb-2">Besturing:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-400">
                  <div>• Links/Rechts: bewegen</div>
                  <div>• Omhoog: roteren</div>
                  <div>• Omlaag: sneller</div>
                  <div>• Spatiebalk: drop</div>
                  <div>• P: pauze</div>
                </div>
              </div>

              {gameOver && (
                <div className="mt-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
                  <p className="text-center mb-3">
                    {score > personalBest ? (
                      <span className="text-blue-400 font-bold">Nieuwe persoonlijke record!</span>
                    ) : (
                      <span>Je score: {score}</span>
                    )}
                  </p>
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={handleSubmitScore}
                    disabled={submitting}
                  >
                    {submitting ? 'Opslaan...' : 'Score Opslaan'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-blue-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Medal className="w-5 h-5 text-blue-400" />
                Persoonlijk Record
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-400">{personalBest}</p>
            </CardContent>
          </Card>

          <Card className="border-blue-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {leaderboard.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">Nog geen scores</p>
                ) : (
                  leaderboard.map((entry, index) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-2 bg-gray-800/50 rounded"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={index < 3 ? 'text-yellow-400' : ''}>
                          #{index + 1}
                        </Badge>
                        <div>
                          <p className="font-semibold">{entry.username}</p>
                          <p className="text-xs text-gray-400">
                            {formatDate(entry.created_at)}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-blue-400">{entry.score}</span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
